import { isOrHandleAsExternalUrl } from "../../../helpers";
import type { TIconTheme, TImage, TImageLoading } from "../../../types";
import Link from "../link/link";
import styles from "./teaser-card.module.less";
import { useTranslation } from "react-i18next";
import { MotionProps, m as motion } from "framer-motion";
import { useAnimateSlideToTop } from "../../../animation/animation";
import { THeadingTypes } from "../../../types/heading-types";
import Heading from "../heading/heading";

export type TTeaserCardProps = {
  linkUrl?: string;
  linkTarget?: string;
  image?: TImage;
  imageLoading?: TImageLoading;
  date?: string;
  eyebrow?: string;
  heading?: string;
  text?: string;
  topIcon?: string;
  topIconTheme?: TIconTheme;
  animation?: MotionProps;
  semanticHeadingType?: THeadingTypes;
};

const BaseCard = ({
  image,
  imageLoading,
  heading,
  text,
  linkUrl,
  eyebrow,
  date,
  topIcon,
  topIconTheme = "standard",
  semanticHeadingType = "h3",
  animation,
}: TTeaserCardProps) => {
  const hasTopIcon = topIcon && !image?.url;
  const withoutImage = !image && !hasTopIcon;
  const slideToTopAnimation = useAnimateSlideToTop({ whileInView: true, easing: "easeInOut" });

  const HeadingType = semanticHeadingType;

  return (
    <motion.div {...animation} className={[styles.wrapper, withoutImage ? styles.withoutImage : ""].join(" ")}>
      {image?.url && (
        <div className={styles.imageWrapper}>
          <img className={styles.image} src={image.url} alt={image.alt} loading={imageLoading} />
        </div>
      )}
      {hasTopIcon && (
        <motion.div {...slideToTopAnimation} className={[styles.topIconWrapper, styles[topIconTheme]].join(" ")}>
          <span className={[styles.topIcon, topIcon].join(" ")} />
        </motion.div>
      )}
      <div className={[styles.textWrapper, hasTopIcon ? styles.withTopIcon : ""].join(" ")}>
        {(date || eyebrow) && (
          <div className={styles.tagDateWrapper}>
            {date && <p className={[styles.date, "eyebrow"].join(" ")}>{date}</p>}
            {eyebrow && <p className={[styles.eyebrow, "eyebrow"].join(" ")}>{eyebrow}</p>}
          </div>
        )}
        {heading && (
          <Heading semanticType={semanticHeadingType} visualType={"h5"} className={styles.heading}>
            {heading}
          </Heading>
        )}
        {text && <p className={[styles.text, "body-s"].join(" ")}>{text}</p>}
        {linkUrl && <span className={[styles.linkIcon, "ui-icon-arrow-right"].join(" ")} />}
      </div>
    </motion.div>
  );
};

export const TeaserCard = (props: TTeaserCardProps) => {
  const { t } = useTranslation();
  const { linkUrl, linkTarget, heading } = props;

  if (linkUrl) {
    return (
      <Link
        ariaLabel={`${t("CLICK_TO_READ_MORE_ABOUT")} ${heading}`}
        className={[styles.outerWrapper, styles.withLink].join(" ")}
        url={linkUrl}
        target={isOrHandleAsExternalUrl(linkUrl, linkTarget) ? "_blank" : "_self"}
      >
        <BaseCard {...props} />
      </Link>
    );
  }

  return (
    <div className={styles.outerWrapper}>
      <BaseCard {...props} />
    </div>
  );
};

export default TeaserCard;
